import React, { useState } from "react";
import {
  CognitoIdentityProviderClient,
  SignUpCommand,
  ConfirmSignUpCommand,
  ResendConfirmationCodeCommand,
  AdminDeleteUserCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./baseStyles.css";
import "./SignUpPage.css";

const PASSWORD_HINT =
  "Use at least 8 characters with uppercase, lowercase, a number, and a symbol (no spaces).";

const LOGINID_HINT = "Used in some communications.";

const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])[^\s]{8,}$/;


// remove spaces and common invisible chars users paste on mobile
const sanitizePassword = (pw = "") =>
  pw
    .normalize("NFKC")
    .replace(/[\u200B-\u200D\uFEFF]/g, "") // zero-width chars
    .replace(/\u00A0/g, " ")               // NBSP → space
    .replace(/\s+/g, "");                  // remove ALL whitespace






// helpers
const trim = (s) => (s ?? "").trim();
const looksLikeEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s || "");

/** Convert an email to a dashed, non-email handle
 *  "hernandez@soultstreet.com" -> "hernandez-soultstreet-com"
 */
const emailToDashedHandle = (email) => {
  const lower = String(email).toLowerCase();
  const [local = "", domain = ""] = lower.split("@");
  const dashed = (local + "-" + domain.replace(/\./g, "-"))
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return dashed || "user-" + Date.now();
};

const mapCognitoError = (err) => {
  const name = err?.name || err?.__type || "Error";
  const msg = String(err?.message || "").toLowerCase();

  if (name === "UsernameExistsException") {
    return "That login ID already exists. Enter your confirmation code below or click “Reset Account” to start over.";
  }
  if (
    name === "InvalidPasswordException" ||
    (name === "InvalidParameterException" &&
      (msg.includes("password") ||
        msg.includes("constraint") ||
        msg.includes("regular expression") ||
        msg.includes("pattern")))
  ) {
    return `Password doesn’t meet requirements. ${PASSWORD_HINT}`;
  }
  if (name === "InvalidParameterException" && msg.includes("email")) {
    return "Please enter a valid email address.";
  }
  if (name === "InvalidParameterException" && msg.includes("username cannot be of email format")) {
    return "Login ID can’t be an email address. Use a non-email Login ID.";
  }
  if (name === "CodeMismatchException") return "That confirmation code is incorrect. Double-check and try again.";
  if (name === "ExpiredCodeException") return "That confirmation code has expired. Click “Resend Confirmation Code.”";
  if (name === "LimitExceededException") return "Too many attempts. Please wait a minute and try again.";
  if (name === "UserNotFoundException") return "We couldn’t find an account with that login ID.";
  if (name === "NotAuthorizedException" && msg.includes("confirmed")) return "This account is already confirmed. Please log in.";

  return err?.message || "Something went wrong. Please try again.";
};

const SignUpPage = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    loginUserId: "",
    password: "",
    confirmPassword: "",
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
  });

  // values we persist for the confirm screen
  const [signupUsername, setSignupUsername] = useState(
    localStorage.getItem("signup_username") || ""
  );
  const [signupEmail, setSignupEmail] = useState(
    localStorage.getItem("signup_email") || ""
  );
  const [displayConfirmId, setDisplayConfirmId] = useState(
    localStorage.getItem("signup_display_id") || ""
  );

  const [confirmationCode, setConfirmationCode] = useState("");
  const [isConfirming, setIsConfirming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showResetButton, setShowResetButton] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]:
        name === "password" || name === "confirmPassword"
          ? sanitizePassword(value)
          : (value || ""),
    }));
  };


  const handleSignUp = async () => {
    // Trim inputs
    const loginUserIdRaw = trim(form.loginUserId);
    const emailRaw = trim(form.email);
    const firstName = trim(form.firstName);
    const lastName = trim(form.lastName);

    const password = sanitizePassword(form.password);
    const confirmPassword = sanitizePassword(form.confirmPassword);


    if (!loginUserIdRaw || !password || !emailRaw || !firstName || !lastName) {
      setError("Please fill out all required fields.");
      return;
    }
    if (!looksLikeEmail(emailRaw)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!PASSWORD_REGEX.test(password)) {
      setError(`Password doesn’t meet requirements. ${PASSWORD_HINT}`);
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    // Rule:
    // - If Login ID === Email -> convert to handle (username), display email on confirm
    // - If Login ID looks like an email but != Email -> block
    let usernameForCognito = loginUserIdRaw;
    let displayForConfirm = loginUserIdRaw; // what we show on the confirm screen

    if (loginUserIdRaw.toLowerCase() === emailRaw.toLowerCase()) {
      usernameForCognito = emailToDashedHandle(emailRaw);
      displayForConfirm = emailRaw; // show email to user
    } else if (looksLikeEmail(loginUserIdRaw)) {
      setError("Can’t use an email address for Login ID.");
      return;
    }

    // persist for confirm page + refresh safety
    setSignupUsername(usernameForCognito);
    setSignupEmail(emailRaw);
    setDisplayConfirmId(displayForConfirm);
    localStorage.setItem("signup_username", usernameForCognito);
    localStorage.setItem("signup_email", emailRaw);
    localStorage.setItem("signup_display_id", displayForConfirm);

    setError("");
    setSuccessMessage("");
    setLoading(true);

    try {
      const client = new CognitoIdentityProviderClient({ region: "us-east-1" });

      const userAttributes = [
        { Name: "email", Value: emailRaw },
        { Name: "name", Value: `${firstName} ${lastName}` },
      ];

      const command = new SignUpCommand({
        ClientId: "7883r6l59fuadm2gkgtr4tihve",
        Username: usernameForCognito,        // <-- send HANDLE to Cognito
        Password: password,
        UserAttributes: userAttributes,
      });

      await client.send(command);

      setSuccessMessage("Sign-up successful! Please check your email for a confirmation code.");
      setIsConfirming(true);
      setShowResetButton(false);
      setConfirmationCode("");
      setTimeout(() => document.getElementById("confirmationCode")?.focus(), 0);
    } catch (err) {
      console.error("❌ Sign-up failed:", err);
      if (err?.name === "UsernameExistsException") {
        setIsConfirming(false);
        setShowResetButton(true);
        setConfirmationCode("");
        setSuccessMessage("");
        setError(
          'That login ID already exists. If you already created this account, click “Enter Code” to confirm or “Resend Confirmation Code”. Otherwise click “Reset Account” to start over.'
        );
      } else {
        setError(mapCognitoError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAccount = async () => {
    const username = trim(signupUsername || form.loginUserId);
    if (!username || !confirmationCode) {
      setError("Please enter your Login ID and Confirmation Code.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const client = new CognitoIdentityProviderClient({ region: "us-east-1" });
      const confirmCommand = new ConfirmSignUpCommand({
        ClientId: "7883r6l59fuadm2gkgtr4tihve",
        Username: username,                 // <-- ALWAYS submit the stored HANDLE
        ConfirmationCode: confirmationCode,
      });

      await client.send(confirmCommand);
      setSuccessMessage("Account confirmed! Please log in to complete setup.");
      // optional cleanup
      localStorage.removeItem("signup_username");
      localStorage.removeItem("signup_email");
      localStorage.removeItem("signup_display_id");
      navigate("/login", { state: { message: "Account confirmed! Please log in." } });
    } catch (err) {
      console.error("❌ Confirmation failed:", err);
      setError(mapCognitoError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmationCode = async () => {
    const username = trim(signupUsername || form.loginUserId);
    if (!username) {
      setError("Please enter your Login ID to resend the confirmation code.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const client = new CognitoIdentityProviderClient({ region: "us-east-1" });
      const command = new ResendConfirmationCodeCommand({
        ClientId: "7883r6l59fuadm2gkgtr4tihve",
        Username: username,                // <-- ALWAYS the HANDLE
      });

      await client.send(command);
      setSuccessMessage(`Confirmation code resent! Check your email: ${signupEmail || ""}`);
    } catch (err) {
      console.error("❌ Resend failed:", err);
      setError(mapCognitoError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResetAccount = async () => {
    const username = trim(signupUsername || form.loginUserId);
    setError("");
    setLoading(true);

    try {
      const client = new CognitoIdentityProviderClient({ region: "us-east-1" });
      const command = new AdminDeleteUserCommand({
        UserPoolId: "us-east-1_EqRjL9222",
        Username: username,               // <-- delete by HANDLE
      });

      await client.send(command);
      setSuccessMessage("Your account has been reset. Please sign up again.");
      setIsConfirming(false);
      setShowResetButton(false);
      localStorage.removeItem("signup_username");
      localStorage.removeItem("signup_email");
      localStorage.removeItem("signup_display_id");
    } catch (err) {
      console.error("❌ Account reset failed:", err);
      setError(mapCognitoError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <h2>{isConfirming ? "Confirm Your Account" : "Sign Up"}</h2>

      <div className="form-group">
        {!isConfirming ? (
          <>
            <label className="field-label" htmlFor="loginUserId">Login ID</label>
            <input
              id="loginUserId"
              type="text"
              name="loginUserId"
              placeholder="Choose a Username"
              value={form.loginUserId}
              onChange={handleChange}
              autoComplete="username"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
            />
            <div id="passwordHint" className="hint">{LOGINID_HINT}</div>
            <br />

            <label className="field-label" htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              name="password"
              placeholder="Enter Password"
              value={form.password}
              onChange={handleChange}
              onKeyDown={(e) => { if (e.key === " ") e.preventDefault(); }}
              autoComplete="new-password"
              aria-describedby="passwordHint"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
            />

            <label className="field-label" htmlFor="confirmPassword">Confirm Password</label>
            <input
              id="confirmPassword"
              type="password"
              name="confirmPassword"
              placeholder="Re-enter Password"
              value={form.confirmPassword}
              onChange={handleChange}
              onKeyDown={(e) => { if (e.key === " ") e.preventDefault(); }}
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
            />
            <div id="passwordHint" className="hint">{PASSWORD_HINT}</div>

            <br /><br />

            <label className="field-label" htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              name="email"
              placeholder="Enter Email"
              value={form.email}
              onChange={handleChange}
              autoComplete="email"
            />

            <label className="field-label" htmlFor="firstName">First Name</label>
            <input
              id="firstName"
              type="text"
              name="firstName"
              placeholder="First Name"
              value={form.firstName}
              onChange={handleChange}
              autoComplete="given-name"
            />

            <label className="field-label" htmlFor="lastName">Last Name</label>
            <input
              id="lastName"
              type="text"
              name="lastName"
              placeholder="Last Name"
              value={form.lastName}
              onChange={handleChange}
              autoComplete="family-name"
            />
          </>
        ) : (
          <>
            {/* DISPLAY email if user typed email as Login ID, otherwise display Login ID.
                This field is read-only. We ALWAYS submit signupUsername to Cognito. */}
            <label className="field-label" htmlFor="confirmLogin">Login ID</label>
            <input
              id="confirmLogin"
              type="text"
              value={displayConfirmId || ""}
              readOnly
              autoComplete="username"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
            />
            <div className="hint">
              {signupEmail ? `We sent a code to ${signupEmail}.` : ""}
            </div>

            <label className="field-label" htmlFor="confirmationCode">Confirmation Code</label>
            <input
              id="confirmationCode"
              type="text"
              name="confirmationCode"
              placeholder="6-digit code"
              value={confirmationCode}
              onChange={(e) =>
                setConfirmationCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              inputMode="numeric"
              pattern="\d*"
              maxLength={6}
              autoComplete="one-time-code"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              autoFocus
            />
          </>
        )}
      </div>

      <div className="button-container">
        {!isConfirming ? (
          <button onClick={handleSignUp} disabled={loading}>Sign Up</button>
        ) : (
          <>
            <button onClick={handleConfirmAccount} disabled={loading}>Confirm Account</button>
            <button onClick={handleResendConfirmationCode} disabled={loading}>Resend Confirmation Code</button>
            {showResetButton && (
              <button onClick={handleResetAccount} disabled={loading}>Reset Account</button>
            )}
          </>
        )}
      </div>

      {!isConfirming && /login id already exists/i.test(error || "") && (
        <div className="button-container" style={{ marginTop: 8 }}>
          <button
            type="button"
            onClick={() => {
              setIsConfirming(true);
              setConfirmationCode("");
              setTimeout(() => document.getElementById("confirmationCode")?.focus(), 0);
            }}
            disabled={loading}
          >
            Enter Code
          </button>
          <button type="button" onClick={handleResendConfirmationCode} disabled={loading}>
            Resend Confirmation Code
          </button>
          {showResetButton && (
            <button type="button" onClick={handleResetAccount} disabled={loading}>
              Reset Account
            </button>
          )}
        </div>
      )}

      {error && <div className="error-message" role="alert">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}
    </div>
  );
};

export default SignUpPage;
